import DataFieldCache from "@/models/data.field.cache";
import {DEFINITIONTYPE_POPULATION} from "@/auxillary/rules";
import {isParentFieldType, isRootParentFieldType} from "@/auxillary/datafield.cache.helpers";
import {dataFieldsEditModule} from "@/store";

export enum DataFieldChangeType {
    UPDATE,
    DELETE,
    NONE
}

export interface IDataFieldChangeReport {
    dataFieldCache: DataFieldCache,
    changeType: DataFieldChangeType,
    needsReasonForChange: boolean,
    isPopulatedField: boolean,
    isReadonly: boolean,
    hasChildFieldsWithValues: boolean,
    childFields: Array<IDataFieldChangeReport> | null,
}

export const fieldsWithValuesWillBeChanged = function (dataFields: Array<DataFieldCache>,
                                                       anyParentNotVisible: boolean = false, anyParentVisibilityChanged: boolean = false,
                                                       willBeDeleted = false): Array<IDataFieldChangeReport> {
    const fieldsWithChangedValues: Array<IDataFieldChangeReport> = [];

    for (const dataField of dataFields) {
        // new fields don't need a reason for change
        if (dataField.isNewField)
            continue;

        if (dataField.definitionType == "EventReportListEntry" && dataFieldsEditModule.editingAeReportId != dataField.id)
            continue;

        // we are only interested if the field was previously visible
        // if it was invisible and is again invisible, then there won't be any value changes
        const fieldNotVisible = anyParentNotVisible || !dataField.isVisible;
        const fieldWasNotVisible = !dataField.wasVisible;
        // to catch new populations on fields that are not visible and never were visible (were always empty)
        if (fieldNotVisible && fieldWasNotVisible)
            continue;

        const isParentType = isParentFieldType(dataField.definitionType);
        const fieldHasValueOrIsParent =  (dataField.currentValues?.value != {} &&
            dataField.currentValues?.value != undefined) || isParentType;

        let changedChildFields: Array<IDataFieldChangeReport> = [];

        // check and add children of field if it is not a deleted list entry
        // removingChildren (willBeDeleted = true) are deleted list entries
        // list entry only gets a reason on the highest lvl
        if (isParentType && dataField.childFields.length > 0 && !willBeDeleted)
            changedChildFields = fieldsWithValuesWillBeChanged(dataField.childFields,
                anyParentNotVisible || fieldNotVisible, anyParentVisibilityChanged || dataField.hasVisibilityChanges, willBeDeleted);

        if (dataField.removingChildren.length > 0) {
            changedChildFields.push(...fieldsWithValuesWillBeChanged(dataField.removingChildren,
                anyParentNotVisible || fieldNotVisible, anyParentVisibilityChanged || dataField.hasVisibilityChanges, true));
        }

        // if it is a through visibility deleted list entry, we want a reason for that entry even if all its child fields are empty
        const fieldWillBeDeleted = willBeDeleted || fieldNotVisible &&
            (fieldHasValueOrIsParent && (!isRootParentFieldType(dataField.definitionType) || isParentType && changedChildFields.length > 0));

        if (fieldGetsNewValue(anyParentNotVisible, anyParentVisibilityChanged, dataField)) {
            fieldsWithChangedValues.push({
                dataFieldCache: dataField,
                changeType: getFieldChangeType(
                    anyParentNotVisible, anyParentVisibilityChanged, dataField, willBeDeleted),
                needsReasonForChange: !isRootParentFieldType(dataField.definitionType),
                isPopulatedField: dataField.rules.some(rule => rule.definitionType == DEFINITIONTYPE_POPULATION && !rule.isDeleted),
                isReadonly: dataField.definitionType == "DisplayValue",
                hasChildFieldsWithValues: isParentType,
                childFields: changedChildFields,
            })
            continue;
        } else if (fieldWillBeDeleted) {
            fieldsWithChangedValues.push({
                dataFieldCache: dataField,
                changeType: DataFieldChangeType.DELETE,
                needsReasonForChange: !isRootParentFieldType(dataField.definitionType),
                isPopulatedField: dataField.rules.some(rule => rule.definitionType == DEFINITIONTYPE_POPULATION && !rule.isDeleted),
                isReadonly: dataField.definitionType == "DisplayValue",
                hasChildFieldsWithValues: isParentType,
                childFields: changedChildFields,
            })
            continue;
        }

        if (changedChildFields.length> 0) {
            fieldsWithChangedValues.push({
                dataFieldCache: dataField,
                changeType: DataFieldChangeType.NONE,
                needsReasonForChange: false,
                hasChildFieldsWithValues: true,
                isPopulatedField: false,
                isReadonly: dataField.definitionType == "DisplayValue",
                childFields: changedChildFields
            })
        }
    }

    return fieldsWithChangedValues;
}

export const fieldGetsNewValue = function (
    anyParentNotVisible: boolean, anyParentVisibilityChanged: boolean, dataField: DataFieldCache) {
    // any parent field just became invisible
    // this will change the field, if it was visible
    if (anyParentNotVisible && anyParentVisibilityChanged && dataField.wasVisible) {
        return dataField.currentValues?.value != undefined && dataField.currentValues?.value != {};
    }
    // the field itself just became invisible
    if (!dataField.isVisible && dataField.hasVisibilityChanges) {
        return dataField.currentValues?.value != undefined && dataField.currentValues?.value != {};
    }
    // to catch temporary population changes:
    // the field is and was not visible, so no actual value change
    if (!dataField.isVisible && !dataField.hasVisibilityChanges) {
        return false;
    }
    // to catch temporary population changes:
    // a parent is not visible, but it was already like this, so no actual value change
    if (anyParentNotVisible && !anyParentVisibilityChanged) {
        return false;
    }
    return dataField.hasChanges;
}

export const getFieldChangeType = function (anyParentNotVisible: boolean, anyParentVisibilityChanged: boolean, dataField: DataFieldCache, willBeDeleted: boolean): DataFieldChangeType {
    if (willBeDeleted)
        return DataFieldChangeType.DELETE;

    // any parent field just became invisible, so the value will be deleted if it exists
    if (anyParentNotVisible && anyParentVisibilityChanged && dataField.isVisible) {
        return DataFieldChangeType.DELETE;
    }
    // field just became invisible, so the value will be deleted if it exists
    if (!dataField.isVisible && dataField.hasVisibilityChanges) {
        return DataFieldChangeType.DELETE;
    }
    return DataFieldChangeType.UPDATE;
}
